/**
 * CloseDealModal.tsx — the close ceremony (slice 3a, §5.C).
 *
 * The ONLY UI path into closed_won/closed_lost (decision #7): the board's
 * closed-column drop and card kebab, and the Detail header actions, all open
 * this modal. Outcome is preset by the caller — no toggle inside (switching
 * mid-modal invites the S2-4 400; cancel and re-invoke instead). The Lost
 * variant carries the SB-1 slug dropdown (vocab.ts owns options + labels)
 * plus the note field: required exactly when the slug is 'other', optional
 * otherwise — the lostCloseReady gate mirrors the server rules, so the
 * confirm can't submit a combination the API refuses. The Won variant has
 * no reason fields at all — not hidden-but-posted: sending either with a
 * won close is a named 400.
 *
 * On success the caller receives the WIRE deal (it carries issuedInvoiceId
 * on a won close) and maps it to its own view model.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import type { SalesDeal as WireDeal } from '../../../../api/types'
import { closeDeal } from '../../api'
import { refreshSalesFigures } from '../../salesFigures'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { overlay } from '../../../../theme/tokens'
import { money } from '../../../../lib/money'
import type { CloseOutcome } from './closeFlow'
import { LOST_REASON_OPTIONS, lostCloseReady } from './vocab'

export interface CloseDealSummary {
  id: string
  title: string
  company: string
  /** Integer PENCE (D9's money of record), in the deal's ORIGINAL currency. */
  amountPence: number
  /** ISO 4217 code of amountPence (0111: non-GBP deals exist once captured). */
  currency: string
}

export function CloseDealModal({ deal, outcome, onCancel, onClosed }: {
  deal: CloseDealSummary
  outcome: CloseOutcome
  onCancel: () => void
  onClosed: (wire: WireDeal) => void
}) {
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onCancel])

  const won = outcome === 'won'
  const confirmable = !busy && (won || lostCloseReady(reason, note))

  async function confirm() {
    if (!confirmable) return
    setBusy(true)
    try {
      const wire = await closeDeal(deal.id, outcome,
        won ? undefined : reason,
        won || note.trim() === '' ? undefined : note.trim())
      toast.success(won ? 'Deal closed — draft invoice created in Issued Invoices' : 'Deal closed')
      // Row 84: a close takes a deal out of the open set — re-read the figures
      // the board header and the sidebar count share (board and Deal Detail).
      void refreshSalesFigures()
      onClosed(wire)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to close deal'))
      setBusy(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      {/* S-Tier (slice 3): a centered dialog is a floating widget — .k-card
          (18px, --panel, --shadow-card); the summary box is a nested tile. */}
      <div role="dialog" aria-modal="true" aria-label={won ? 'Close deal as Won' : 'Close deal as Lost'}
        className="k-card"
        style={{ width: 'min(440px, calc(100vw - 32px))' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-edge-row">
          <div className="text-detail-title font-bold text-ink-primary">
            {won ? 'Close deal as Won' : 'Close deal as Lost'}
          </div>
          <button onClick={onCancel} aria-label="Cancel"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          {/* Read-only deal summary */}
          <div className="rounded-tile border border-edge-row bg-surface-panel px-4 py-3">
            <div className="text-13 font-semibold text-ink-primary">{deal.title}</div>
            <div className="text-12 text-ink-secondary mt-1">
              {deal.company} · {money(deal.amountPence / 100, deal.currency)}
            </div>
          </div>

          {won ? (
            <div className="text-12 text-ink-secondary">
              Creates a draft invoice in Finance → Issued Invoices for {money(deal.amountPence / 100, deal.currency)} — it is not sent.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-12 font-semibold text-ink-body mb-2">Lost reason *</label>
                <select value={reason} onChange={e => setReason(e.target.value)} autoFocus
                  className="w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body focus:outline-none focus:border-accent">
                  <option value="">Select a reason…</option>
                  {LOST_REASON_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-12 font-semibold text-ink-body mb-2">
                  {reason === 'other' ? 'Note *' : 'Note'}
                </label>
                <textarea value={note} onChange={e => setNote(e.target.value)}
                  placeholder={reason === 'other' ? 'What happened? Required for “Other”.' : 'Optional detail'} rows={2}
                  className="w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent resize-none" />
              </div>
              <div className="text-12 text-ink-secondary">
                This deal moves to the Archive. No invoice is created.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 pb-5">
          <button onClick={onCancel} className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={confirm} disabled={!confirmable}
            className="k-btn k-btn--lg disabled:opacity-60">
            {busy ? 'Closing…' : won ? 'Close as Won' : 'Close as Lost'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
